import React, { useMemo } from "react";
import { Trans, t } from "@lingui/macro";
import { BlueSwitch, MenuList, MenuListItemVariant } from "@WahooFitness/redesignr";
import { useRemoteSensors } from "@/hooks/useRemoteSensors";
import { Box, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { crux_bolt_type_e } from "@WahooFitness/crux-js-display-cfg";

interface TrackrRadarConfigProps {
  appToken: string;
}

const TrackrRadarConfig: React.FC<TrackrRadarConfigProps> = ({ appToken }) => {
  const {
    device,
    remoteSensorAppConfig,
    saveAppRadarConfig,
    updateOnScreenRadarAlertLocation,
    updateRadarAlertLocation,
  } = useRemoteSensors(appToken);
  const { palette } = useTheme();

  const isROAM2 = device?.boltType === crux_bolt_type_e.ROAM_2;

  const radarAlertScreenLocationListItem = useMemo(() => {
    const radarScreenAlertLocationValue = remoteSensorAppConfig?.cfg?.app?.radar_cfg
      ?.alerts_on_screen_right_enabled
      ? "Right"
      : "Left";

    return {
      id: "radar_alert_screen_location",
      content: isROAM2 ? t`Location on screen` : t`Car representation on screen`,
      variant: MenuListItemVariant.NoAction,
      secondaryContent: (
        <Box display="flex" flexDirection="column" rowGap={1} mt={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={radarScreenAlertLocationValue}
            onChange={updateOnScreenRadarAlertLocation}
          >
            <ToggleButton value={"Left"}>
              <Trans>Left</Trans>
            </ToggleButton>
            <ToggleButton value={"Right"}>
              <Trans>Right</Trans>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ),
      hidden:
        !remoteSensorAppConfig?.cfg.app?.radar_cfg?.alerts_enabled ||
        (isROAM2 && !remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_on_screen_enabled),
    };
  }, [
    isROAM2,
    remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_enabled,
    remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_on_screen_enabled,
    remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_on_screen_right_enabled,
    updateOnScreenRadarAlertLocation,
  ]);

  const radarAlertDeviceLocationListItem = useMemo(() => {
    const radarScreenAlertLocationValue = remoteSensorAppConfig?.cfg?.app?.radar_cfg
      ?.alerts_on_left_leds_enabled
      ? "Left LEDs"
      : "On screen";

    return {
      id: "radar_alert_device_location",
      content: isROAM2 ? t`Car representation` : t`Location on screen`,
      variant: MenuListItemVariant.NoAction,
      secondaryContent: (
        <Box display="flex" flexDirection="column" rowGap={1} mt={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={radarScreenAlertLocationValue}
            onChange={updateRadarAlertLocation}
          >
            <ToggleButton value={"Left LEDs"}>
              <Trans>Left LEDs</Trans>
            </ToggleButton>
            <ToggleButton value={"On screen"}>
              <Trans>On screen</Trans>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ),
      hidden: !isROAM2 || !remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_enabled,
      hasDivider: !remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_on_screen_enabled,
    };
  }, [
    isROAM2,
    remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_enabled,
    remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_on_left_leds_enabled,
    remoteSensorAppConfig?.cfg?.app?.radar_cfg?.alerts_on_screen_enabled,
    updateRadarAlertLocation,
  ]);

  if (!remoteSensorAppConfig) {
    return null;
  }

  const supportsLEDs =
    device?.boltType &&
    [
      crux_bolt_type_e.ROAM_2,
      crux_bolt_type_e.ROAM_1_1,
      crux_bolt_type_e.ROAM,
      crux_bolt_type_e.BOLT_2,
      crux_bolt_type_e.BOLT,
      crux_bolt_type_e.ELEMNT,
    ].includes(device.boltType);

  return (
    <Box>
      <MenuList
        listItems={[
          {
            id: "alert_on_elemnt",
            content: t`Alerts on ELEMNT`,
            variant: MenuListItemVariant.Action,
            disabled: remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_enabled === undefined,
            actionComponent: (
              <BlueSwitch checked={remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_enabled} />
            ),
            action: () =>
              saveAppRadarConfig({
                alerts_enabled: !remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_enabled,
              }),
          },
          radarAlertDeviceLocationListItem,
          radarAlertScreenLocationListItem,
          {
            id: "sounds_on_elemnt",
            content: t`Sounds`,
            variant: MenuListItemVariant.Action,
            disabled: remoteSensorAppConfig.cfg?.app?.radar_cfg?.alert_sounds_enabled === undefined,
            hidden: !remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_enabled,
            actionComponent: (
              <BlueSwitch
                checked={remoteSensorAppConfig.cfg?.app?.radar_cfg?.alert_sounds_enabled}
              />
            ),
            action: () =>
              saveAppRadarConfig({
                alert_sounds_enabled:
                  !remoteSensorAppConfig.cfg?.app?.radar_cfg?.alert_sounds_enabled,
              }),
          },
          {
            id: "radar_led_status",
            content: t`Show radar status in top LEDs`,
            variant: MenuListItemVariant.Action,
            hidden:
              !supportsLEDs ||
              !remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_enabled ||
              remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_on_screen_enabled,
            disabled:
              remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_on_top_leds_enabled === undefined,
            actionComponent: (
              <BlueSwitch
                checked={remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_on_top_leds_enabled}
              />
            ),
            action: () =>
              saveAppRadarConfig({
                alerts_on_top_leds_enabled:
                  !remoteSensorAppConfig.cfg?.app?.radar_cfg?.alerts_on_top_leds_enabled,
              }),
          },
        ]}
        disableGutters
      />
      {supportsLEDs && (
        <Box mx={2}>
          <Typography variant="ui-base-medium" color={palette.text.secondary}>
            <Trans>
              Amber when a car passes normally, red when a car passes dangerously, green when all
              clear
            </Trans>
          </Typography>
        </Box>
      )}
      {/* Add other config options here
        <MenuList
        listItems={[
          {
            id: "approaching_alerts",
            content: t`Vehicle approaching alerts`,
            secondaryContent: t`Flash when vehicle approaches`,
            variant: MenuListItemVariant.Action,
            actionComponent: (
              <BlueSwitch checked={remoteSensorAppConfig.cfg.app.radar_cfg.whatever} />
            ),
            action: () =>
              saveAppRadarConfig({
                whatever: !remoteSensorAppConfig.cfg.app.radar_cfg.whatever,
              }),
          },
        ]}
        disableGutters
      /> */}
    </Box>
  );
};

export { TrackrRadarConfig };
